import React, { useRef, useEffect } from "react";
import { Container } from "@pixi/react";
import { useSelector } from "react-redux";
import { useReelRefs } from "../../Contexts/ReelRefsContext";
import { useSlot } from "../../Contexts/SlotRegistryContext";
import RoyalCollectRespinCounterController from "./RoyalCollectRespinCounterController";
import {
  selectRoyalCollectActive,
  selectRoyalCollectRespinsLeft,
} from "../../State/selectors";

/**
 * RoyalCollectRespinCounter
 * Driven entirely by Redux state — single source of truth.
 *
 * - royalCollectRespinsLeft changes → animate to new value
 * - royalCollectActive goes false → animate to 0
 *
 * Redux flow:
 *   rgsPlay.fulfilled (intro)            → sets royalCollectRespinsLeft = 3
 *   rgsPlay.fulfilled (active)           → decrements royalCollectRespinsLeft by 1
 *                                           + stores pending value from API
 *   applyRoyalCollectPendingRespins      → applies pending (reset to 3 when first coin lands)
 *
 * The component just follows Redux. No manual "set to 3". No RAF polling.
 */
const RoyalCollectRespinCounter = () => {
  const royalCollectActive = useSelector(selectRoyalCollectActive);
  const royalCollectRespinsLeft = useSelector(selectRoyalCollectRespinsLeft);

  const controllerRef = useRef(null);
  const containerRef = useRef(null);

  const { royalCollectRespinCounterRef } = useReelRefs();
  const bonusCounterSlot = useSlot("reelfront.rc.bonus_counter");

  // Initialize controller
  useEffect(() => {
    if (containerRef.current && !controllerRef.current) {
      controllerRef.current = new RoyalCollectRespinCounterController(
        containerRef.current,
      );
      royalCollectRespinCounterRef.current = controllerRef.current;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Inject into ReelFront's bonus_counter slot via SlotRegistry
  useEffect(() => {
    if (!bonusCounterSlot || !containerRef.current) return;
    const node = containerRef.current;
    bonusCounterSlot.addChild(node);
    return () => {
      if (node.parent === bonusCounterSlot) {
        bonusCounterSlot.removeChild(node);
      }
    };
  }, [bonusCounterSlot]);

  // Drive counter entirely from Redux state.
  // This single effect handles:
  //   - Initial show (0 → 3 when intro rgsPlay.fulfilled fires)
  //   - Decrement (3 → 2 when active rgsPlay.fulfilled fires)
  //   - Reset (2 → 3 when applyRoyalCollectPendingRespins fires on first coin landing)
  //   - Feature end (N → 0 when royalCollectActive goes false)
  //
  // The controller's animation queue handles deduplication and queueing
  // internally — calling playAnimation(X) when displayedValue is already X
  // is a no-op. No refs needed to guard against double-fires.
  useEffect(() => {
    const ctrl = controllerRef.current;
    if (!ctrl?.isReady()) return;

    if (!royalCollectActive) {
      ctrl.playAnimation(0);
      return;
    }

    ctrl.playAnimation(royalCollectRespinsLeft);
  }, [royalCollectRespinsLeft, royalCollectActive]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (controllerRef.current) {
        controllerRef.current.cleanup();
        controllerRef.current = null;
      }
      royalCollectRespinCounterRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container
      ref={containerRef}
      name="royal_collect_respin_counter"
      sortableChildren
    />
  );
};

export default RoyalCollectRespinCounter;
